import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { changeTab } from 'store/slices/menu/menuSlice';
import userManageSelect from 'store/slices/userManage/userManage.selector';
import { selectUserAccount } from 'store/slices/currentUser/currentUser.selector';
import { selectActiveGroupId } from 'store/slices/cognito/cognito.selector';
import { selectAccessToken } from 'store/slices/auth/auth.selector';
import {
  fetchSelectedUser,
  fetchTableManageColumns,
} from 'store/slices/userManage/userManageThunk';

import { ITableRow, UserListsProps } from './UserLists.props';
import UserListsView from './UserLists.view';
import { IUserTableList } from 'interfaces/usertablelist.interface';

const UserLists = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUserAccount);
  const userActiveGroupID = useAppSelector(selectActiveGroupId);
  const palmettoToken = useAppSelector(selectAccessToken);
  const groupLists = useAppSelector(userManageSelect.selectGroupLists);
  const navigate = useNavigate();
  const location = useLocation();

  const isSuperAdmin = useMemo(() => currentUser?.pvAdministrator === 1, [currentUser]);

  const [defaultFilters, setDefaultFilters] = useState<string[] | null>(null);

  const fetchColumnsData = async (): Promise<IManageColumns> => {
    return await dispatch(fetchTableManageColumns()).unwrap();
  };

  function getUserDataFilter(refreshFilters?: { filters: string[] }) {
    const filters = [];
    if (!isSuperAdmin) {
      filters.push(`activeGroup=${userActiveGroupID}`);
    }
    if (refreshFilters && refreshFilters.filters) {
      refreshFilters.filters.forEach((filter) => {
        filters.push(filter);
      });
    }
    return filters;
  }

  const forceGridRefresh = () => {
    const today = new Date();
    const time = Math.floor(today.getTime() / 1000);
    const refreshFilters = { filters: ['time=' + time] };
    const updatedFilters = getUserDataFilter(refreshFilters);

    setDefaultFilters(updatedFilters);
  };

  useEffect(() => {
    forceGridRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Trigger refresh when returning from user view/edit pages
    if (location.state?.shouldRefresh) {
      forceGridRefresh();
      // Clear the state to prevent refresh on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const onRowSelect = (rows: ITableRow) => {
    const { row } = rows;
    if (row && row.id) {
      dispatch(fetchSelectedUser(Number(row.id)));
      navigate(`/user/view/${row.id}`);
    }
    dispatch(changeTab({ tabName: 'user' }));
  };

  const onMobileRowSelect = (rows: IUserTableList) => {
    const { row } = rows;
    if (row && row.id) {
      dispatch(fetchSelectedUser(Number(row.id)));
      navigate(`/user/view/${row.id}`);
      dispatch(changeTab({ tabName: 'user' }));
    }
  };

  const handleNewUser = () => {
    navigate('/user/new');
  };

  const combineProps: UserListsProps = {
    accessToken: palmettoToken || '',
    defaultFilters,
    handleNewUser,
    onRowSelect,
    onMobileRowSelect,
    isSuperAdmin,
    userActiveGroupID,
    groupLists,
    fetchColumnsData,
  };

  return <UserListsView {...combineProps} />;
};

export default UserLists;
